import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  CircularProgress,
  Autocomplete,
  Divider,
  Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { businessAPI, geocodeAPI, referenceAPI } from '../services/api';
import { useBusiness } from '../context/BusinessContext';
import { US_STATES } from '../constants/usStates';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { cardSx } from '../theme';
import { FormSkeleton } from '../components/LoadingSkeletons';

const BusinessPage = () => {
  const { updateBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    business_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    logo_url: '',
    website: '',
    facebook: '',
    instagram: '',
    twitter: '',
    tax_id: '',
    home_office_pct: '',
    mileage_category_id: '',
    sale_signer_name: '',
    sale_signer_title: '',
    hours_of_operation: '',
    coordinates: '',
  });
  const [geocoding, setGeocoding] = useState(false);
  const [expenseCategories, setExpenseCategories] = useState([]);

  useEffect(() => {
    loadBusiness();
    referenceAPI.getExpenseTypeCategories()
      .then((response) => setExpenseCategories(response.data))
      .catch((error) => console.error('Error loading expense categories:', error));
  }, []);

  const loadBusiness = async () => {
    try {
      const response = await businessAPI.get();
      
      // Convert hours_of_operation from JSON to string for display
      const hoursString = response.data.hours_of_operation
        ? JSON.stringify(response.data.hours_of_operation, null, 2)
        : '';
      const coordinatesString =
        response.data.latitude != null && response.data.longitude != null
          ? `${response.data.latitude}, ${response.data.longitude}`
          : '';

      setFormData({
        business_name: response.data.business_name || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zip_code: response.data.zip_code || '',
        logo_url: response.data.logo_url || '',
        website: response.data.website || '',
        facebook: response.data.facebook || '',
        instagram: response.data.instagram || '',
        twitter: response.data.twitter || '',
        tax_id: response.data.tax_id || '',
        home_office_pct:
          response.data.home_office_pct != null ? String(response.data.home_office_pct) : '',
        mileage_category_id:
          response.data.mileage_category_id != null ? String(response.data.mileage_category_id) : '',
        sale_signer_name: response.data.sale_signer_name || '',
        sale_signer_title: response.data.sale_signer_title || '',
        hours_of_operation: hoursString,
        coordinates: coordinatesString,
      });
    } catch (error) {
      console.error('Error loading business:', error);
      setErrorMessage(error.response?.data?.error || 'Error loading business information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const lookupCoordinates = async () => {
    const queryParts = [formData.address, formData.city, formData.state, formData.zip_code].filter(Boolean);
    if (!queryParts.length) return;

    setGeocoding(true);
    try {
      const { data } = await geocodeAPI.lookup(queryParts.join(', '));
      setFormData((prev) => ({ ...prev, coordinates: `${data.lat}, ${data.lng}` }));
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Could not reach the geocoding service.');
    } finally {
      setGeocoding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Parse hours_of_operation back to JSON if provided
      const dataToSubmit = { ...formData };

      // Blank means "not set" rather than 0% -- an empty string would coerce
      // to zero and silently zero out every prorated expense in the tax report.
      if (formData.home_office_pct === '') {
        dataToSubmit.home_office_pct = null;
      } else {
        const pct = Number(formData.home_office_pct);
        if (Number.isNaN(pct) || pct < 0 || pct > 100) {
          setErrorMessage('Home office percentage must be between 0 and 100');
          setSaving(false);
          return;
        }
        dataToSubmit.home_office_pct = pct;
      }

      dataToSubmit.mileage_category_id =
        formData.mileage_category_id === '' ? null : Number(formData.mileage_category_id);

      if (formData.hours_of_operation) {
        try {
          dataToSubmit.hours_of_operation = JSON.parse(formData.hours_of_operation);
        } catch (err) {
          setErrorMessage('Invalid JSON format for business hours');
          setSaving(false);
          return;
        }
      } else {
        dataToSubmit.hours_of_operation = null;
      }

      const response = await businessAPI.update(dataToSubmit);
      setSuccessMessage('Business information saved successfully');
      // Update the business context so header updates immediately
      updateBusiness(response.data);
      loadBusiness();
    } catch (error) {
      console.error('Error saving business:', error);
      setErrorMessage(error.response?.data?.error || 'Error saving business information');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>Business Information</Typography>
        <FormSkeleton fields={8} label="Loading business information…" />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Business Information</Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      <Paper sx={{ ...cardSx, p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                name="business_name"
                label="Business Name"
                value={formData.business_name}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                name="phone"
                label="Phone"
                value={formData.phone}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                name="tax_id"
                label="Tax ID / EIN"
                value={formData.tax_id}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                name="home_office_pct"
                label="Home Office %"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                value={formData.home_office_pct}
                onChange={handleInputChange}
                helperText="Share of the house's square footage used for the business. Applied in the tax report to expense types marked as prorated."
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                fullWidth
                options={expenseCategories}
                getOptionLabel={(category) => category.name}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                filterOptions={createPrefixFilter((category) => category.name)}
                value={expenseCategories.find(
                  (category) => String(category.id) === formData.mileage_category_id
                ) ?? null}
                onChange={(event, newValue) =>
                  setFormData((prev) => ({
                    ...prev,
                    mileage_category_id: newValue ? String(newValue.id) : '',
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Mileage Category"
                    helperText="Expense category the Tax Workbook groups mileage deductions under."
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Address
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                size="small"
                name="address"
                label="Street Address"
                value={formData.address}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                size="small"
                name="city"
                label="City"
                value={formData.city}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={US_STATES}
                getOptionLabel={(option) => option.name}
                filterOptions={createPrefixFilter((option) => option.name)}
                value={US_STATES.find(state => state.code === formData.state) || null}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, state: newValue ? newValue.code : '' }));
                }}
                renderInput={(params) => (
                  <TextField
                    size="small"
                    {...params}
                    label="State"
                    fullWidth
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <Box component="li" key={key} {...otherProps}>
                      {option.code} - {option.name}
                    </Box>
                  );
                }}
                isOptionEqualToValue={(option, value) => option.code === value?.code}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                size="small"
                name="zip_code"
                label="Zip Code"
                value={formData.zip_code}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  name="coordinates"
                  label="Coordinates (Latitude, Longitude)"
                  value={formData.coordinates}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="e.g., 35.48086, -97.75584"
                  helperText="The ranch's location, used as the start/end point for the mileage deduction in the Tax Year Workbook."
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={lookupCoordinates}
                  disabled={geocoding || !formData.address}
                  sx={{ minWidth: 'auto', px: 0.5, height: 40, flexShrink: 0 }}
                  title="Look up coordinates from the address above"
                >
                  {geocoding ? <CircularProgress size={16} /> : <MyLocationIcon fontSize="small" />}
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Horse Sales
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                name="sale_signer_name"
                label="Seller Name"
                value={formData.sale_signer_name}
                onChange={handleInputChange}
                fullWidth
                helperText="Printed under the seller signature on sales agreements"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                name="sale_signer_title"
                label="Seller Title"
                value={formData.sale_signer_title}
                onChange={handleInputChange}
                fullWidth
                placeholder="Owner"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Online Presence
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                name="website"
                label="Website URL"
                value={formData.website}
                onChange={handleInputChange}
                fullWidth
                placeholder="https://www.example.com"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                name="logo_url"
                label="Logo URL"
                value={formData.logo_url}
                onChange={handleInputChange}
                fullWidth
                placeholder="https://www.example.com/logo.png"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                size="small"
                name="facebook"
                label="Facebook URL"
                value={formData.facebook}
                onChange={handleInputChange}
                fullWidth
                placeholder="https://facebook.com/yourpage"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                size="small"
                name="instagram"
                label="Instagram URL"
                value={formData.instagram}
                onChange={handleInputChange}
                fullWidth
                placeholder="https://instagram.com/yourpage"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                size="small"
                name="twitter"
                label="Twitter URL"
                value={formData.twitter}
                onChange={handleInputChange}
                fullWidth
                placeholder="https://twitter.com/yourpage"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Business Hours
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                size="small"
                name="hours_of_operation"
                label="Hours of Operation (JSON format)"
                value={formData.hours_of_operation}
                onChange={handleInputChange}
                multiline
                rows={6}
                fullWidth
                placeholder='{"Monday": "9:00 AM - 5:00 PM", "Tuesday": "9:00 AM - 5:00 PM"}'
                helperText="Enter business hours in JSON format"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default BusinessPage;




